import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/session";
import { FiMessageCircle } from "react-icons/fi";

import ApiChat from "../../adapters/apiChat";
import ApiLog from "../../adapters/apiLog";
import commonFunctions from "../../library/commonFunctions";
import constants from "../../library/constants";
import { useChatHistory } from "../../contexts/ChatHistoryContext";

import Composer from "./chat/Composer";
import EmptyState from "./chat/EmptyState";
import MarketPicker from "./chat/MarketPicker";
import MessageList from "./chat/MessageList";
import VerbosityToggle from "./chat/VerbosityToggle";
import {
  marketLabel,
  countryInRegion,
  defaultCountryForRegion,
} from "./chat/markets";

/**
 * Verbosity is sent as a prose instruction because that is what the Product
 * API expects; the AgentCore adapter maps it back to an enum by keyword. Do
 * not reword these without checking ApiChat.mapVerbosity.
 */
const VERBOSITY_INSTRUCTIONS = {
  0: "IMPORTANT: Keep your response very brief and concise - maximum 2-3 sentences. No lengthy explanations or lists.",
  1: "Provide a clear, focused response. Use a short paragraph or a concise bullet list where appropriate. Aim for 250-400 words maximum — no lengthy preamble, no exhaustive lists.",
  2: "Please provide a detailed, comprehensive explanation with examples where helpful. Aim for 400-600 words maximum.",
};

// REGION_DEFAULT_COUNTRY used to live here, duplicating the first country of
// each region from markets.js. defaultCountryForRegion derives it instead, so
// adding a market is a one-file change.

const transcriptKey = (persistKey) => `tnsiq_transcript:${persistKey}`;

let messageSeq = 0;
const nextId = () => {
  messageSeq += 1;
  return `m${Date.now().toString(36)}-${messageSeq}`;
};

const readTranscript = (persistKey) => {
  if (!persistKey) return [];
  try {
    const raw = sessionStorage.getItem(transcriptKey(persistKey));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/**
 * TNSPay IQ chat.
 *
 * Rendered by three pages, so it supports two layouts:
 *   layout="fullheight" — /ask. Fills its parent, scrolls only the transcript,
 *                         pins the composer to the bottom.
 *   layout="inline"     — Solution Builder and Marketing Content Builder,
 *                         where it sits inside a normal-flow card. The
 *                         transcript grows the page and the composer sticks
 *                         near the bottom of the viewport.
 */
export default function ChatComponent(props) {
  const {
    question,
    region,
    layout = "inline",
    starterPrompts = [],
    persistKey = null,
    resetSignal = 0,
    questionNonce = 0,
    // A saved conversation to open, from the recent-questions rail. The nonce
    // lets the same conversation be reopened after navigating within it.
    loadThreadId = null,
    loadNonce = 0,
  } = props;

  const isFullHeight = layout === "fullheight";

  const [messages, setMessages] = useState(() => readTranscript(persistKey));
  const [input, setInput] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [countryContext, setCountryContext] = useState("usa");
  const [selectedCountry, setSelectedCountry] = useState("United States");
  const [verbosity, setVerbosity] = useState(1);
  const [toast, setToast] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [initialized, setInitialized] = useState(false);

  const apiChat = useRef(new ApiChat());
  const apiLog = useRef(new ApiLog());
  const auth = useAuth();
  const { addChatEntry } = useChatHistory();

  const stoppedRef = useRef(false);
  const busyRef = useRef(false);
  const lastQuestionSignature = useRef(null);
  // Reads current market/verbosity without making submitQuestion depend on
  // them, so a settings change mid-answer cannot restart anything.
  const settingsRef = useRef({ countryContext, selectedCountry, verbosity });
  settingsRef.current = { countryContext, selectedCountry, verbosity };

  /* ------------------------------------------------------------- logging */
  const log = useCallback(
    async (logcontext, message) => {
      try {
        await apiLog.current.logsCreate({
          id: commonFunctions.getDateTimeNow(),
          type: "info",
          user: auth.user?.profile?.email,
          logcontext,
          message,
        });
      } catch (error) {
        console.error("Error logging:", error?.response?.data?.error);
      }
    },
    [auth.user]
  );

  /* ------------------------------------------------------------- sending */
  const submitQuestion = useCallback(
    async (questionText, options = {}) => {
      const text = (questionText || "").trim();
      if (!text || busyRef.current) return;

      const { replaceFrom = null } = options;
      const { countryContext: market, selectedCountry: country, verbosity: level } =
        settingsRef.current;

      busyRef.current = true;
      stoppedRef.current = false;
      setIsBusy(true);
      setStreamingContent("");
      setAnnouncement("");

      const userMessage = {
        id: nextId(),
        role: "user",
        content: text,
        region: market,
        country,
        market: marketLabel(market, country),
        timestamp: Date.now(),
      };

      // The question lands in the transcript before the request is made, so a
      // failure leaves it on screen to retry. The old flow only added a turn
      // on success, so a failed question disappeared entirely.
      setMessages((prev) => {
        const base = replaceFrom === null ? prev : prev.slice(0, replaceFrom);
        return [...base, userMessage];
      });

      const request = {
        current_query: text,
        verbosity_instruction: VERBOSITY_INSTRUCTIONS[level],
        // Numeric level as well as the prose. The AgentCore front door takes a
        // closed enum rather than free text (so the client cannot inject
        // arbitrary instructions), and inferring the level from the prose is
        // ambiguous: level 1 also contains the word "concise", so keyword
        // sniffing misreads it as the shortest setting.
        verbosity_level: level,
        user_email: auth.user?.profile?.email || "anonymous",
      };

      try {
        await apiChat.current.init();

        const response = await apiChat.current.MessagesCreate(
          request,
          market,
          country,
          (chunk, fullResponse) => setStreamingContent(fullResponse)
        );

        const answer = response.data.message || response.data.summary || "";
        const wasStopped = stoppedRef.current;

        if (!answer) {
          throw new Error("The assistant returned an empty response.");
        }

        const assistantMessage = {
          id: nextId(),
          role: "assistant",
          content: answer,
          timestamp: Date.now(),
          status: wasStopped || response.data.partial ? "partial" : undefined,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        // threadId comes from localStorage rather than being tracked here,
        // because apiChat writes it from the session_id the server returns and
        // is therefore the authority on it. Including it lets the rail replace
        // the row for this conversation instead of stacking a new one per
        // follow-up, and lets a later click resume rather than re-ask.
        addChatEntry({
          text,
          response: answer,
          timestamp: Date.now(),
          threadId: localStorage.getItem(constants.localStorage.threadId) || null,
        });
        setAnnouncement(
          wasStopped ? "Response stopped." : "Response ready."
        );
      } catch (error) {
        // A user-initiated stop before any text arrived is not an error; the
        // question simply stays in the transcript, ready to retry.
        if (!stoppedRef.current) {
          const detail =
            error?.response?.data?.error ||
            error?.message ||
            "Something went wrong while answering that. Please try again.";

          setMessages((prev) => [
            ...prev,
            {
              id: nextId(),
              role: "assistant",
              content: detail,
              timestamp: Date.now(),
              status: "error",
            },
          ]);
          setAnnouncement(`Request failed. ${detail}`);
          log("chat component error", detail);
        }
      } finally {
        setStreamingContent("");
        setIsBusy(false);
        busyRef.current = false;
        stoppedRef.current = false;
        log("question", text);
      }
    },
    [addChatEntry, auth.user, log]
  );

  const handleStop = useCallback(() => {
    stoppedRef.current = true;
    apiChat.current.cancelActiveRequest();
  }, []);

  const handleSend = useCallback(
    (text) => {
      setInput("");
      submitQuestion(text);
    },
    [submitQuestion]
  );

  /**
   * Re-ask the question that produced a given assistant turn, dropping that
   * turn and its question so the retry replaces them rather than stacking a
   * near-duplicate exchange onto the transcript.
   */
  const handleRetry = useCallback(
    (message, index) => {
      const priorUser = [...messages.slice(0, index)]
        .reverse()
        .find((m) => m.role === "user");
      if (!priorUser) return;
      const userIndex = messages.lastIndexOf(priorUser);
      submitQuestion(priorUser.content, { replaceFrom: userIndex });
    },
    [messages, submitQuestion]
  );

  const handleShare = useCallback(
    async (message, index) => {
      const priorUser = [...messages.slice(0, index)]
        .reverse()
        .find((m) => m.role === "user");
      if (!priorUser) return;

      const shareUrl = `${window.location.origin}/?question=${encodeURIComponent(
        priorUser.content
      )}&country=${priorUser.region || countryContext}`;

      try {
        await navigator.clipboard.writeText(shareUrl);
        setToast("Link copied. Share it by email, SMS or Teams.");
        setTimeout(() => setToast(""), 2400);
      } catch (error) {
        console.error("Failed to copy:", error);
        setToast("Could not copy the link.");
        setTimeout(() => setToast(""), 2400);
      }
    },
    [messages, countryContext]
  );

  const resetConversation = useCallback(() => {
    apiChat.current.cancelActiveRequest();
    setMessages([]);
    setStreamingContent("");
    setInput("");
    // Drop the server-side thread too, otherwise "New chat" clears the screen
    // while the agent keeps answering in the context of the old conversation.
    localStorage.removeItem(constants.localStorage.threadId);
    localStorage.removeItem("kendra_session_id");
    if (persistKey) {
      try {
        sessionStorage.removeItem(transcriptKey(persistKey));
      } catch (error) {
        /* storage unavailable: nothing to clear */
      }
    }
  }, [persistKey]);

  /* --------------------------------------------------------- persistence */
  useEffect(() => {
    if (!persistKey) return;
    try {
      sessionStorage.setItem(transcriptKey(persistKey), JSON.stringify(messages));
    } catch (error) {
      /* quota or private mode: the transcript just will not survive a reload */
    }
  }, [messages, persistKey]);

  /* ------------------------------------------------------------ lifecycle */
  // Restore the market preference, then drain a shared ?question= link.
  useEffect(() => {
    if (initialized) return;

    if (region) {
      localStorage.setItem(constants.localStorage.countrypref, region);
      setCountryContext(region);
      setSelectedCountry(defaultCountryForRegion(region));
    } else {
      const savedRegion = localStorage.getItem(constants.localStorage.countrypref);
      const savedCountry = localStorage.getItem(constants.localStorage.selectedcountry);
      if (savedRegion) {
        setCountryContext(savedRegion);
        // The saved pair is checked, not trusted. These are two independent
        // localStorage keys written at different times, so a mismatched pair
        // already on disk would otherwise survive every reload -- and anyone
        // who hit the region-change bug above has one saved right now.
        setSelectedCountry(
          countryInRegion(savedRegion, savedCountry)
            ? savedCountry
            : defaultCountryForRegion(savedRegion)
        );
      }
    }

    const sharedQuestion = localStorage.getItem(
      constants.localStorage.sharedquestion
    );
    if (sharedQuestion) {
      localStorage.removeItem(constants.localStorage.sharedquestion);
      lastQuestionSignature.current = `shared::${sharedQuestion}`;
      submitQuestion(sharedQuestion);
    }

    setInitialized(true);
  }, [initialized, region, submitQuestion]);

  // Questions pushed in from outside: starter prompts, the history rail, and
  // the builder pages' question generators. The nonce lets the same string be
  // sent twice in a row, which the old effect silently swallowed.
  useEffect(() => {
    if (!initialized || !question) return;
    const signature = `${questionNonce}::${question}`;
    if (lastQuestionSignature.current === signature) return;
    lastQuestionSignature.current = signature;
    submitQuestion(question);
  }, [initialized, question, questionNonce, submitQuestion]);

  // "New chat" from the rail.
  useEffect(() => {
    if (!resetSignal) return;
    resetConversation();
  }, [resetSignal, resetConversation]);

  // Open a saved conversation from the rail.
  //
  // Loads and displays it. Nothing is sent to the agent: this is the fix for
  // clicking a recent question re-asking it. Pointing the stored thread id at
  // the loaded conversation afterwards means the next thing typed continues it
  // rather than starting somewhere new.
  useEffect(() => {
    if (!loadNonce || !loadThreadId) return;

    let cancelled = false;

    (async () => {
      // Abandon anything in flight; the user has navigated away from it.
      apiChat.current.cancelActiveRequest();
      setIsBusy(false);
      busyRef.current = false;
      setStreamingContent("");
      setAnnouncement("Loading conversation…");

      try {
        const { messages: loaded, threadId } =
          await apiChat.current.getThreadMessages(loadThreadId);
        if (cancelled) return;

        setMessages(
          loaded.map((m) => ({
            id: nextId(),
            role: m.role,
            content: m.text,
            timestamp: m.timestamp ?? Date.now(),
          }))
        );
        localStorage.setItem(constants.localStorage.threadId, threadId);
        setAnnouncement(
          loaded.length
            ? `Conversation loaded, ${loaded.length} messages.`
            : "That conversation has no saved messages."
        );
      } catch (error) {
        if (cancelled) return;
        // Left on screen rather than silently clearing the transcript: losing
        // what is already displayed because a fetch failed is worse than the
        // failure.
        setToast(error.message ?? "Could not open that conversation");
        setAnnouncement("Could not open that conversation.");
      }
    })();

    return () => { cancelled = true; };
  }, [loadThreadId, loadNonce]);

  /* ------------------------------------------------------------ rendering */
  // Changing the region must also move the country into it. Without this the
  // country kept its previous value: choosing Brazil and then switching to
  // APAC left region=apac with country=Brazil, which the chip rendered as
  // "APAC" (marketLabel falls back to the region name when the country is not
  // in it) while the request still asked for Brazil.
  const handleRegionChange = (nextRegion) => {
    setCountryContext(nextRegion);
    localStorage.setItem(constants.localStorage.countrypref, nextRegion);

    if (!countryInRegion(nextRegion, settingsRef.current.selectedCountry)) {
      const nextCountry = defaultCountryForRegion(nextRegion);
      setSelectedCountry(nextCountry);
      localStorage.setItem(constants.localStorage.selectedcountry, nextCountry);
    }
  };

  const handleCountryChange = (nextCountry) => {
    setSelectedCountry(nextCountry);
    localStorage.setItem(constants.localStorage.selectedcountry, nextCountry);
  };

  const composer = (
    <>
      <Composer
        value={input}
        onChange={setInput}
        onSubmit={handleSend}
        onStop={handleStop}
        isBusy={isBusy}
        canStop={isBusy}
        autoFocus={isFullHeight}
        footer={
          <>
            <div className="flex items-center gap-1">
              <MarketPicker
                selectedRegion={countryContext}
                selectedCountry={selectedCountry}
                onRegionChange={handleRegionChange}
                onCountryChange={handleCountryChange}
              />
              {!isFullHeight && messages.length > 0 && (
                <button
                  type="button"
                  onClick={resetConversation}
                  className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12.5px] font-medium text-iq-ink-soft transition-colors hover:bg-iq-rail hover:text-iq-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-iq-brand"
                >
                  <FiMessageCircle className="h-3.5 w-3.5" />
                  New chat
                </button>
              )}
            </div>
            <VerbosityToggle value={verbosity} onChange={setVerbosity} />
          </>
        }
      />
      <p className="mt-2 text-center text-[11.5px] text-iq-ink-faint">
        Verify details before sharing them with a customer.
      </p>
    </>
  );

  const transcript = (
    <MessageList
      messages={messages}
      streamingContent={streamingContent}
      isThinking={isBusy}
      scrollable={isFullHeight}
      onRetry={handleRetry}
      onShare={handleShare}
      emptyState={
        isFullHeight ? (
          <EmptyState
            prompts={starterPrompts.slice(0, 4)}
            disabled={isBusy}
            onPromptSelect={handleSend}
          />
        ) : null
      }
    />
  );

  const liveRegion = (
    <div aria-live="polite" className="sr-only">
      {announcement}
    </div>
  );

  const toastNode = toast ? (
    <div
      role="status"
      className="fixed bottom-5 right-5 z-50 rounded-lg bg-iq-ink px-4 py-2.5 text-[13px] text-white shadow-lg"
    >
      {toast}
    </div>
  ) : null;

  if (isFullHeight) {
    return (
      <div className="iq-chat flex h-full min-h-0 flex-col bg-iq-canvas font-noto-sans text-iq-ink">
        {transcript}
        <div className="shrink-0 border-t border-iq-line bg-iq-canvas px-4 pb-3 pt-3">
          <div className="mx-auto w-full max-w-3xl">{composer}</div>
        </div>
        {liveRegion}
        {toastNode}
      </div>
    );
  }

  return (
    <div className="iq-chat font-noto-sans text-iq-ink">
      {transcript}
      <div className="sticky bottom-3 z-10 mt-4">
        <div className="mx-auto w-full max-w-3xl">{composer}</div>
      </div>
      {liveRegion}
      {toastNode}
    </div>
  );
}
